import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Col, Container } from "reactstrap";
import { smartScalesColumns } from "pages/settings/smart-scales/SmartScalesColumns";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import NGITable from "components/Common/NGITable";
import CustomButton from "components/Common/buttons/CustomButton";
import { FILTER_PAGE } from "utils/constants";
import SmartScaleSearch from "pages/settings/smart-scales/SmartScaleSearch";
import { getSmartScalesList, removeSmartScale } from "utils/api";
import useStoreFilters from "customHooks/useStoreFilters";
import { isEmpty } from "lodash";
import { getDictionary } from "store/dictionaries/actions";
import { DICTIONARY_TYPES } from "store/dictionaries/constants";
import AddSmartScaleModal from "pages/settings/smart-scales/AddSmartScaleModal";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";

const SmartScalesList = () => {
  const dispatch = useDispatch();
  const [openAddModal, setOpenAddModal] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [isLoading, setLoader] = useState(false);
  const [list, setList] = useState([]);
  const [activeItem, setActiveItem] = useState(null);
  const { filters, setFilters } = useStoreFilters(FILTER_PAGE.CAMPAIGN);

  useEffect(() => {
    dispatch(getDictionary(DICTIONARY_TYPES.DEPOT));
    !isEmpty(filters) ? handleSearch(filters) : handleSearch();
  }, []);

  const onListLoaded = useCallback(res => {
    const { result } = res?.data;
    if (result?.items?.length === 0) {
      warnMessage("Scales were not found");
    }
    setList(res?.data || null);
    setLoader(false);
  }, []);

  const handleSearch = (filterOptions = {}) => {
    setLoader(true);
    const filters = { ...filterOptions };
    setFilters(filters);
    getSmartScalesList(filters)
      .then(res => onListLoaded(res))
      .catch(err => {
        setLoader(false);
        setList(null);
        defaultNgiErrorMethod(err);
      });
  };

  const onSetActiveItem = useCallback((item, isDelete) => {
    setActiveItem(item);
    !isDelete && onOpenAddModal();
    isDelete && openDeleteConfirmation();
  }, []);

  const openDeleteConfirmation = useCallback(() => setConfirmationOpen(true), []);
  const closeDeleteConfirmation = useCallback(() => {
    setConfirmationOpen(false);
    setActiveItem(null);
  }, []);

  const onOpenAddModal = useCallback(() => setOpenAddModal(true), []);
  const onCloseAddModal = useCallback(() => {
    setOpenAddModal(false);
    setActiveItem(null);
  }, []);

  const onDelete = useCallback(() => {
    setLoader(true);
    removeSmartScale(activeItem.id)
      .then(() => {
        setTimeout(() => {
          successMessage("Smart scale was removed successfully");
          setLoader(false);
          handleSearch();
          setActiveItem(null);
        }, 1000);
      })
      .catch(e => {
        defaultNgiErrorMethod(e);
        setLoader(false);
        setActiveItem(null);
      });
  }, [activeItem]);

  const columns = useMemo(() => smartScalesColumns(onSetActiveItem), []);

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className="filters-wrapper">
            <div className="filter-buttons">
              <CustomButton
                type="primary"
                onClick={onOpenAddModal}
                icon={<i className="bx bx-plus-circle" />}
                text="New Smart Scale"
              />
            </div>

            <SmartScaleSearch handleSearch={handleSearch} />
          </div>
          <Col>
            <div className="p-2">
              <NGITable
                filtersType={FILTER_PAGE.SMART_SCALE}
                updateList={handleSearch}
                isLoading={isLoading}
                columns={columns}
                data={list}
              />
            </div>
          </Col>
        </Container>
        {openAddModal && <AddSmartScaleModal data={activeItem} onCancel={onCloseAddModal} updateList={handleSearch} />}
        {confirmationOpen && (
          <ConfirmationModal
            description={"Are you sure you want to delete this smart scale?"}
            onCancel={closeDeleteConfirmation}
            onConfirm={onDelete}
          />
        )}
      </div>
    </>
  );
};

export default SmartScalesList;
